import {
  Button,
  Col,
  Form,
  message,
  Modal,
  Result,
  Space,
  Steps,
  theme,
  Typography,
} from "antd";
import { useEffect, useRef, useState } from "react";
import { QueryOperation } from "../code/search-query/search-query";
import {
  Company,
  CompanyIndustry,
  CompanyRole,
} from "../repository/data.default";
import {
  addCompany,
  getCompanies,
  getCompanyDepartments,
  getCompanyIndustries,
  getCompanyUserRoles,
  updateCompany,
} from "../repository/data.repository";
import { retry } from "../utils/http";
import { nameOf } from "../utils/typescript";
import {
  CompanyFormWizard,
  UsersFormWizard,
} from "./CompanyRegistrationFormWizard.default";
import { RegistrationProcessStatus } from "../repository/auth.default";
import Loader from "../controls/Loader";
import {
  registerCompanyUser,
  registerForCompanyWithFilePreview,
} from "../repository/auth.repository";
import dayjs from "dayjs";
import DynamicForm from "../controls/DynamicForm";
import { Guid } from "guid-typescript";
import CompanyRoleCreateDialog from "./CompanyRoleCreateDialog";
import { InputObject } from "../controls/default";

export interface CompanyRegistrationFormWizardProps {
  readonly open?: boolean;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function CompanyRegistrationFormWizard(
  props: CompanyRegistrationFormWizardProps,
) {
  const { token } = theme.useToken();
  const [companyForm] = Form.useForm();
  const companyValues = Form.useWatch<CompanyFormWizard>([], companyForm);
  const [usersForm] = Form.useForm();
  const usersValues = Form.useWatch<UsersFormWizard>([], usersForm);
  const fileRef = useRef<HTMLInputElement>(null!);
  const [messageApi, contextHolder] = message.useMessage();

  const [step, setStep] = useState(0);
  const [company, setCompany] = useState<Company | null>(null);
  // Admin brief 2026-11-04 — bump this whenever a role is created inline so
  // every user row's Role combobox reloads its options and sees the new role.
  const [rolesRefreshKey, setRolesRefreshKey] = useState<string>(Guid.create().toString());
  const [createRoleOpen, setCreateRoleOpen] = useState(false);
  const [createRoleInitialName, setCreateRoleInitialName] = useState<string>("");
  // Resolver that the CreateableComboBox awaits — we fulfil it when the user
  // either confirms (passing the InputObject) or cancels (passing undefined).
  const createRoleResolveRef = useRef<((v: InputObject | undefined) => void) | null>(null);
  const [companyValuesValid, setCompanyValuesValid] = useState(false);
  const [usersValuesValid, setUsersValuesValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [finish, setFinish] = useState(false);
  const [error, setError] = useState(false);
  const [status, setStatus] = useState<"success" | "warning" | "error">(
    "success",
  );
  const [title, setTitle] = useState("");
  const [logs, setLogs] = useState<string[]>([]);

  useEffect(() => {
    if (props.open) {
      companyForm.resetFields();
      usersForm.setFieldsValue({
        usersInfos: [{}],
      });
    } else {
      reset();
    }
  }, [props.open]);

  useEffect(() => {
    companyForm
      .validateFields({
        validateOnly: true,
      })
      .then((e) => {
        setCompanyValuesValid(true);
      })
      .catch(() => {
        setCompanyValuesValid(false);
      });
  }, [companyForm, companyValues, props.open]);

  useEffect(() => {
    usersForm
      .validateFields({
        validateOnly: true,
      })
      .then((e) => {
        setUsersValuesValid(true);
      })
      .catch(() => {
        setUsersValuesValid(false);
      });
  }, [usersForm, usersValues, props.open]);

  // Events

  function onStepBack(): void {
    setStep(step - 1);
  }

  function onStepNext(): void {
    switch (step) {
      case 0:
        {
          setLoading(true);
          setError(false);
          const {
            name,
            address,
            email,
            phoneNumber,
            domain,
            parentCompany,
            registrationNotes,
          } = companyValues;
          if (company?.uid) {
            updateCompany({
              uid: company?.uid!,
              rowVersion: company.rowVersion!,
              name,
              address,
              email,
              phoneNumber,
              // Industry & Parent are fixed once the company is created (step 0) — not re-sent on edit.
            })
              .then((result) => {
                setStep(step + 1);
                setLoading(false);
                setCompany(result);
                usersForm.setFieldsValue({
                  ...usersValues,
                  company: {
                    display: result.name,
                    value: result.uid,
                    dataItem: result,
                  },
                });
              })
              .catch(() => {
                setLoading(false);
                setError(true);
                setStatus("error");
                setTitle("Something went wrong, try again");
              });
          } else {
            addCompany({
              name,
              address,
              email,
              phoneNumber,
              domainUid: domain.value,
              parentCompanyUid: parentCompany?.value,
              registrationNotes,
            })
              .then((result) => {
                setStep(step + 1);
                setLoading(false);
                setCompany(result);
                usersForm.setFieldsValue({
                  ...usersValues,
                  company: {
                    display: result.name,
                    value: result.uid,
                    dataItem: result,
                  },
                });
              })
              .catch(() => {
                setLoading(false);
                setError(true);
                setStatus("error");
                setTitle("Something went wrong, try again");
              });
          }
        }
        break;
      case 1:
        {
          setLoading(true);
          setError(false);
          registerCompanyUser({
            companyUid: company?.uid!,
            usersInfos: usersValues.usersInfos.map(
              ({
                email,
                firstName,
                lastName,
                dateOfBirth,
                role,
                department,
              }) => ({
                email,
                firstName,
                lastName,
                dateOfBirth: dateOfBirth.format("YYYY-MM-DD"),
                roleUid: role.value,
                departmentUid: department.value,
              }),
            ),
          })
            .then((result) => {
              setStep(step + 1);
              setFinish(true);
              setLoading(false);
              setLogs(result.textLog);
              switch (result.status) {
                case RegistrationProcessStatus.Success:
                  setTitle("Registration have been completed successfully");
                  setStatus("success");
                  break;
                case RegistrationProcessStatus.Error:
                  setTitle("Registration failed to be added");
                  setStatus("error");
                  break;
                case RegistrationProcessStatus.Unknown:
                  setTitle("Registration went wrong, try again");
                  setStatus("warning");
                  break;
              }

              props.onFinish?.();
            })
            .catch(() => {
              setLoading(false);
              setError(true);
              setStatus("error");
              setTitle("Something went wrong, try again");
            });
        }
        break;
    }
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  function onReset(): void {
    reset();
  }

  function onImportUsers(): void {
    fileRef.current.click();
  }

  function onImportUsersFile(e: React.ChangeEvent): void {
    const file: File = (e.target as any).files[0];
    if (file) {
      const mb = file.size / 1024 / 1024;
      const limit = 100;
      if (mb > limit) {
        messageApi.open({
          type: "error",
          content: `Excel size limit is ${limit}mb`,
        });
      } else {
        setLoading(true);
        setError(false);
        registerForCompanyWithFilePreview({
          file,
          companyUid: company?.uid!,
        })
          .then(({ usersInfos }) => {
            setLoading(false);
            const values = usersForm.getFieldsValue();
            usersForm.setFieldsValue({
              ...values,
              usersInfos: usersInfos.map(
                ({
                  email,
                  firstName,
                  lastName,
                  dateOfBirth,
                  roleName,
                  roleUid,
                  departmentName,
                  departmentUid,
                }) => {
                  return {
                    email,
                    firstName,
                    lastName,
                    dateOfBirth: dayjs(dateOfBirth, ["YYYY-MM-DD"]),
                    role: {
                      display: roleName,
                      value: roleUid,
                    },
                    department: {
                      display: departmentName,
                      value: departmentUid,
                    },
                  };
                },
              ),
            });
            (e.target as any).value = null;
          })
          .catch(() => {
            setLoading(false);
            setError(true);
            setStatus("error");
            setTitle("Something went wrong, try again");
          });
      }
    }
  }

  // Methods

  const companiesRequest = (search: string) => {
    return retry(() =>
      getCompanies({
        searchQuery: {
          conditions: [
            {
              field: nameOf<Company>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.name,
        value: dataItem.uid,
        dataItem,
      })),
    );
  };

  const industriesRequest = (search: string) => {
    return retry(() =>
      getCompanyIndustries({
        searchQuery: {
          conditions: [
            {
              field: nameOf<CompanyIndustry>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.name,
        value: dataItem.uid,
        dataItem,
      })),
    );
  };

  // Scope role/department lookups to the wizard's chosen company so an operator can't pick another
  // company's role/department (the backend would otherwise reject the registration).
  const companyScopedConditions = (search: string) => {
    const conditions: any[] = [
      {
        field: nameOf<CompanyRole>("name"),
        operation: QueryOperation.StartsWith,
        values: [search],
      },
    ];
    if (company?.uid)
      conditions.push({
        field: "companyUid",
        operation: QueryOperation.Equal,
        values: [company.uid],
      });
    return conditions;
  };

  const userRolesRequest = (search: string) => {
    return retry(() =>
      getCompanyUserRoles({
        searchQuery: {
          conditions: companyScopedConditions(search),
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.name,
        value: dataItem.uid,
        dataItem,
      })),
    );
  };

  // Admin brief 2026-11-04 — inline "Create role" from the Role dropdown.
  // Resolves when the dialog is closed; we auto-select the new role in the
  // row that triggered the dialog, and bump rolesRefreshKey so all other
  // user rows' Role dropdowns reload and see the new role too.
  function onCreateRoleFromCombobox(initialName: string): Promise<InputObject | undefined> {
    if (!company?.uid) {
      messageApi.open({ type: "warning", content: "Finish step 1 (Company) first." });
      return Promise.resolve(undefined);
    }
    setCreateRoleInitialName(initialName);
    setCreateRoleOpen(true);
    return new Promise<InputObject | undefined>((resolve) => {
      createRoleResolveRef.current = resolve;
    });
  }

  function onCreateRoleFinished(created: InputObject): void {
    setCreateRoleOpen(false);
    setRolesRefreshKey(Guid.create().toString());
    createRoleResolveRef.current?.(created);
    createRoleResolveRef.current = null;
  }

  function onCreateRoleCancelled(): void {
    setCreateRoleOpen(false);
    createRoleResolveRef.current?.(undefined);
    createRoleResolveRef.current = null;
  }

  const departmentsRequest = (search: string) => {
    return retry(() =>
      getCompanyDepartments({
        searchQuery: {
          conditions: companyScopedConditions(search),
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.name,
        value: dataItem.uid,
        dataItem,
      })),
    );
  };

  function reset() {
    setStep(0);
    setCompanyValuesValid(false);
    setUsersValuesValid(false);
    setFinish(false);
    setError(false);
    setStatus("success");
    setLogs([]);
    setCompany(null);
    companyForm.resetFields();
    usersForm.setFieldsValue({
      usersInfos: [{} as any],
    });
  }

  function isStepValid() {
    if (step === 0) {
      return companyValuesValid;
    } else if (step === 1) {
      return usersValuesValid;
    } else {
      return true;
    }
  }

  const steps = [
    {
      title: "Company",
    },
    {
      title: "Users",
    },
    {
      title: "Finish",
    },
  ];

  return (
    <>
      <Modal
        open={props.open}
        centered
        title={"Company Registration"}
        footer={null}
        maskClosable={false}
        onCancel={onCancel}
      >
        {/* Steps */}
        <Steps current={step} items={steps} />
        <Col className="flex max-h-[512px] min-w-[392px] max-w-[512px] flex-col overflow-hidden overflow-y-auto">
          {step === 0 && (
            <Col className="flex flex-auto flex-col pb-4 pt-4">
              <DynamicForm
                className="w-auto"
                form={companyForm}
                schema={{
                  groups: [
                    {
                      controls: [
                        {
                          type: "combobox",
                          name: "parentCompany",
                          label: "Parent Company",
                          request: companiesRequest,
                        },
                        {
                          type: "textbox",
                          name: "name",
                          label: "Name",
                          rules: [
                            {
                              required: true,
                              message: "Name is required",
                            },
                          ],
                        },
                        {
                          type: "textbox",
                          name: "address",
                          label: "Address",
                          rules: [
                            {
                              required: false,
                              message: "Address is required",
                            },
                          ],
                        },
                        {
                          type: "textbox",
                          name: "email",
                          label: "Email",
                          rules: [
                            {
                              required: false,
                              message: "Email is required",
                            },
                            {
                              type: "email",
                              message: "Invalid email format",
                            },
                          ],
                        },
                        {
                          type: "textbox",
                          name: "phoneNumber",
                          label: "Phone Number",
                          rules: [
                            {
                              required: false,
                              message: "Phone number is required",
                            },
                          ],
                        },
                        {
                          type: "combobox",
                          name: "domain",
                          label: "Industry",
                          rules: [
                            {
                              required: true,
                              message: "Industry is required",
                            },
                          ],
                          request: industriesRequest,
                        },
                        {
                          type: "areabox",
                          name: "registrationNotes",
                          label: "Registration Notes",
                        },
                      ],
                    },
                  ],
                }}
              />
            </Col>
          )}
          {step == 1 && (
            <Col className="flex flex-auto flex-col pb-4 pt-4">
              <DynamicForm
                className="w-auto"
                form={usersForm}
                schema={{
                  groups: [
                    {
                      controls: [
                        {
                          type: "labelbox",
                          label: "Company",
                          name: "company",
                        },
                      ],
                    },
                    {
                      name: "usersInfos",
                      label: ({ usersInfos }, index) => {
                        const item = (usersInfos || [])[index!];

                        return [item?.firstName, item?.lastName]
                          .filter((f) => !!f)
                          .join(" ");
                      },
                      isList: true,
                      controls: [
                        {
                          type: "textbox",
                          name: "firstName",
                          label: "First Name",
                          rules: [
                            {
                              required: true,
                              message: "First Name is required",
                            },
                          ],
                        },
                        {
                          type: "textbox",
                          name: "lastName",
                          label: "Last Name",
                          rules: [
                            {
                              required: true,
                              message: "Last Name is required",
                            },
                          ],
                        },
                        {
                          type: "textbox",
                          name: "email",
                          label: "Email",
                          rules: [
                            {
                              type: "email",
                              message: "Invalid email format",
                            },
                          ],
                        },
                        {
                          type: "datepicker",
                          name: "dateOfBirth",
                          label: "Date Of Birth",
                          rules: [
                            {
                              required: true,
                              message: "Date Of Birth is required",
                            },
                          ],
                        },
                        {
                          // Admin brief 2026-11-04 — inline role creation.
                          type: "createablecombobox",
                          name: "role",
                          label: "Role",
                          rules: [
                            {
                              required: true,
                              message: "Role is required",
                            },
                          ],
                          request: userRolesRequest,
                          onCreate: onCreateRoleFromCombobox,
                          createLabel: "Create new role",
                          refreshKey: rolesRefreshKey,
                        },
                        {
                          type: "combobox",
                          name: "department",
                          label: "Department",
                          rules: [
                            {
                              required: true,
                              message: "Department is required",
                            },
                          ],
                          request: departmentsRequest,
                        },
                      ],
                    },
                  ],
                }}
              />
              <input
                className="hidden"
                ref={fileRef}
                hidden={true}
                type="file"
                onChange={onImportUsersFile}
              />
            </Col>
          )}
          {step == 2 && (
            <Col className="flex flex-auto flex-col items-center justify-center">
              {!loading && !finish && !error && (
                <Typography.Paragraph>
                  Complete users registration
                </Typography.Paragraph>
              )}
              {loading && !finish && !error && (
                <Typography.Paragraph>
                  User is registering…
                </Typography.Paragraph>
              )}
              {loading && <Loader />}
              {(finish || error) && (
                <Result
                  status={status}
                  title={title}
                  extra={
                    <Typography.Paragraph className="text-start">
                      {logs.map((text, key) => (
                        <Typography.Text
                          className="inline-flex"
                          key={key}
                          type="secondary"
                        >
                          {text}
                        </Typography.Text>
                      ))}
                    </Typography.Paragraph>
                  }
                />
              )}
            </Col>
          )}
        </Col>
        {/* Actions */}
        {!finish && (
          <Space className="flex flex-row items-center justify-end p-2">
            {step === 1 && (
              <>
                <Button onClick={onImportUsers}>Import Users</Button>
              </>
            )}
            <Button disabled={step < 1 && !error} onClick={onStepBack}>
              Back
            </Button>
            <Button
              type="primary"
              disabled={!isStepValid()}
              loading={loading}
              onClick={onStepNext}
            >
              Next
            </Button>
          </Space>
        )}
        {finish && (
          <Space className="flex flex-row items-center justify-end p-2">
            <Button onClick={onCancel}>Close</Button>
            <Button type="primary" onClick={onReset}>
              Company Registration
            </Button>
          </Space>
        )}
      </Modal>
      {company?.uid && (
        <CompanyRoleCreateDialog
          open={createRoleOpen}
          companyUid={company.uid}
          initialName={createRoleInitialName}
          onClose={onCreateRoleCancelled}
          onCreated={onCreateRoleFinished}
        />
      )}
      {contextHolder}
    </>
  );
}

export default CompanyRegistrationFormWizard;
